import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, BACKEND_URL } from "@/lib/api";
import { BadgeCheck, ShieldAlert, ShieldX, Loader2, Search, Phone } from "lucide-react";

const TONE = {
  ACTIVE: { cls: "bg-emerald-500 text-white", icon: BadgeCheck, note: "This worker is verified and currently active with Karmivo." },
  SUSPENDED: { cls: "bg-amber-500 text-slate-950", icon: ShieldAlert, note: "This worker is currently suspended. Do not engage them for Karmivo work." },
  REVOKED: { cls: "bg-red-600 text-white", icon: ShieldX, note: "This Worker ID has been revoked and is no longer valid." },
};

function Lookup() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  return (
    <form className="flex gap-2 mt-6" onSubmit={(e) => { e.preventDefault(); q.trim() && nav(`/verify/${q.trim().toUpperCase()}`); }}>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="KRV-2026-0001" className="bg-zinc-900 border-zinc-700 text-white" data-testid="verify-input"/>
      <Button className="bg-orange-500 hover:bg-orange-600" data-testid="verify-search-btn"><Search size={16}/></Button>
    </form>
  );
}

export default function VerifyWorker() {
  const { code } = useParams();
  const [sp] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!code) return;
    setD(null); setErr("");
    api.get(`/verify/${encodeURIComponent(code)}`, { params: sp.get("t") ? { t: sp.get("t") } : {} })
      .then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Could not verify right now"));
  }, [code, sp]);
  const tone = d && (TONE[d.status] || TONE.REVOKED);
  const Icon = tone?.icon;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="border-b border-zinc-800 px-5 py-4 flex items-center justify-between max-w-xl mx-auto">
        <div className="font-display font-extrabold text-xl tracking-tight">KARMIVO<span className="block h-0.5 w-6 bg-orange-500 mt-0.5"/></div>
        <span className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold">Worker Verification</span>
      </header>
      <main className="max-w-xl mx-auto px-5 py-8">
        {!code && (<><h1 className="font-display text-3xl font-bold">Verify a Karmivo worker</h1>
          <p className="text-zinc-400 text-sm mt-2">Enter the Worker ID printed on the card, or scan its QR code.</p><Lookup/></>)}
        {code && !d && !err && <Loader2 className="animate-spin text-orange-500 mt-10" size={28} data-testid="verify-loading"/>}
        {err && (
          <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 mt-4" data-testid="verify-not-found">
            <ShieldX className="text-red-400" size={28}/>
            <div className="font-bold text-lg mt-2">Not verified</div>
            <p className="text-sm text-red-200/80 mt-1">{err}. This card may be fake. Call Karmivo on 9636873585.</p>
            <Lookup/>
          </div>
        )}
        {d && (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden" data-testid="verify-result">
            <div className={`px-5 py-3 flex items-center gap-2 font-bold tracking-wider ${tone.cls}`} data-testid="verify-status"><Icon size={20}/>{d.status}</div>
            <div className="p-5 flex gap-5">
              {d.photo_available
                ? <img src={`${BACKEND_URL}/api/verify/${d.worker_code}/photo`} alt={d.name} className="w-24 h-32 object-cover rounded-lg border-2 border-orange-500" data-testid="verify-photo"/>
                : <div className="w-24 h-32 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] text-zinc-500 text-center px-2">Photo not shown</div>}
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Worker name</div>
                <div className="font-display text-xl font-bold" data-testid="verify-name">{d.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-3">Worker ID</div>
                <div className="font-mono text-orange-400 font-bold" data-testid="verify-code">{d.worker_code}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-3">Issue date</div>
                <div className="text-sm" data-testid="verify-issue-date">{d.issue_date ? new Date(`${d.issue_date}T00:00:00+05:30`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "-"}</div>
              </div>
            </div>
            <div className="px-5 pb-5">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Registered work categories</div>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="verify-categories">{d.categories.map((c) => <span key={c} className="px-2.5 py-1 rounded-full bg-zinc-800 text-xs">{c}</span>)}</div>
              <p className="text-sm text-zinc-300 mt-5">{tone.note}</p>
              {d.card_check === "replaced" && <p className="text-sm text-amber-300 mt-2" data-testid="verify-card-replaced">This physical card was replaced and is no longer valid. Ask the worker for their current card.</p>}
              {d.card_check === "invalid" && <p className="text-sm text-red-300 mt-2" data-testid="verify-card-invalid">The QR code on this card does not match our records. The card may be fake.</p>}
              <p className="text-[11px] text-zinc-500 mt-4">Live status from Karmivo records · checked {new Date(d.checked_at).toLocaleString("en-IN")}</p>
            </div>
          </div>
        )}
        <a href="tel:9636873585" className="mt-8 flex items-center gap-2 text-sm text-zinc-400"><Phone size={14}/>Karmivo Support: 9636873585 · support@karmivopvt.ltd</a>
      </main>
    </div>
  );
}
